export type ReviewDatum = {
  created_at: string;
  status: string;
};

export type ProviderDatum = {
  provider: string;
  invocations: number;
  successes: number;
  failures: number;
};

export type PullRequestDatum = {
  repository: string;
  number: number;
  created_at?: string;
  author?: string;
};

export type CompletedPullDatum = {
  repo: string;
  number: number;
  state: string;
  created_at?: string;
  merged_at?: string;
  completed_at?: string;
  author?: string;
};

export type AnalyticsFilter = {
  repository: string;
  user: string;
};

export type AnalyticsIdentity = {
  repository?: string;
  author?: string;
};

function sameUser(left?: string, right?: string) {
  return (left ?? "").toLocaleLowerCase() === (right ?? "").toLocaleLowerCase();
}

export function analyticsAuthorIndex(
  openPulls: Array<{ repository: string; number: number; author?: string }>,
  completedPulls: Array<{ repo: string; number: number; author?: string }>,
) {
  const authors = new Map<string, string>();
  for (const pull of completedPulls) {
    if (pull.author) authors.set(`${pull.repo}#${pull.number}`, pull.author);
  }
  for (const pull of openPulls) {
    if (pull.author) authors.set(`${pull.repository}#${pull.number}`, pull.author);
  }
  return authors;
}

function matchesAnalyticsFilter(
  filter: AnalyticsFilter,
  item: AnalyticsIdentity,
) {
  if (filter.repository && item.repository !== filter.repository) return false;
  if (filter.user && (!item.author || !sameUser(item.author, filter.user))) return false;
  return true;
}

export function filterAnalyticsItems<T>(
  items: T[],
  filter: AnalyticsFilter,
  identity: (item: T) => AnalyticsIdentity,
) {
  if (!filter.repository && !filter.user) return items;
  return items.filter((item) => matchesAnalyticsFilter(filter, identity(item)));
}

function uniqueAnalyticsNames(names: Array<string | undefined>) {
  const seen = new Map<string, string>();
  for (const name of names) {
    const trimmed = name?.trim();
    if (!trimmed) continue;
    const key = trimmed.toLocaleLowerCase();
    if (!seen.has(key)) seen.set(key, trimmed);
  }
  return [...seen.values()].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: "base" })
  );
}

export function analyticsRepositoryChoices(names: Array<string | undefined>) {
  return uniqueAnalyticsNames(names);
}

export function analyticsUserChoices(
  identities: AnalyticsIdentity[],
  filter: AnalyticsFilter,
) {
  return uniqueAnalyticsNames(
    identities
      .filter((item) => !filter.repository || item.repository === filter.repository)
      .map((item) => item.author),
  );
}

export type ReviewAnalyticsDatum = ReviewDatum & {
  repository: string;
  number: number;
  trigger?: string;
  started_at?: string;
  provider_finished_at?: string;
  completed_at?: string;
  result?: Record<string, unknown>;
};

export type FindingAnalyticsDatum = {
  repository: string;
  number: number;
  priority?: string;
  created_at: string;
};

export type ProviderEventDatum = {
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
};

export type PullThreadDatum = {
  repository: string;
  number: number;
  unresolved_threads?: number;
};

function utcDateRows<T>(
  now: Date,
  days: number,
  values: (date: string, label: string) => T,
) {
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(now);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCDate(date.getUTCDate() - (days - index - 1));
    const key = date.toISOString().slice(0, 10);
    const label = date.toLocaleDateString("en", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return values(key, label);
  });
}

export function analyticsHistoryDays(
  timestamps: Array<string | undefined>,
  now = new Date(),
  minimumDays = 14,
) {
  const oldest = timestamps.reduce<number | undefined>((current, value) => {
    const timestamp = value ? Date.parse(value) : Number.NaN;
    if (!Number.isFinite(timestamp)) return current;
    return current === undefined ? timestamp : Math.min(current, timestamp);
  }, undefined);
  if (oldest === undefined) return minimumDays;
  const today = new Date(now);
  today.setUTCHours(0, 0, 0, 0);
  const firstDay = new Date(oldest);
  firstDay.setUTCHours(0, 0, 0, 0);
  const recordedDays = Math.floor(
    (today.getTime() - firstDay.getTime()) / 86_400_000,
  ) + 1;
  return Math.max(minimumDays, recordedDays);
}

export function reviewThroughput(
  reviews: ReviewDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    reviews: 0,
    failures: 0,
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const review of reviews) {
    const row = byDate.get(review.created_at.slice(0, 10));
    if (!row) continue;
    row.reviews += 1;
    if (["failed", "aborted"].includes(review.status)) row.failures += 1;
  }
  return rows;
}

export function pullRequestFlow(
  openPulls: PullRequestDatum[],
  completedPulls: CompletedPullDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    created: 0,
    merged: 0,
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  const created = new Map<string, string>();
  for (const pull of [...openPulls, ...completedPulls.map((pull) => ({
    repository: pull.repo,
    number: pull.number,
    created_at: pull.created_at,
  }))]) {
    if (!pull.created_at) continue;
    const key = `${pull.repository}#${pull.number}`;
    if (!created.has(key)) created.set(key, pull.created_at);
  }
  for (const createdAt of created.values()) {
    const row = byDate.get(createdAt.slice(0, 10));
    if (row) row.created += 1;
  }
  const merged = new Set<string>();
  for (const pull of completedPulls) {
    if (pull.state !== "merged") continue;
    const mergedAt = pull.merged_at || pull.completed_at;
    const key = `${pull.repo}#${pull.number}`;
    const row = mergedAt ? byDate.get(mergedAt.slice(0, 10)) : undefined;
    if (!row || merged.has(key)) continue;
    merged.add(key);
    row.merged += 1;
  }
  return rows;
}

export function reviewDurationTrend(
  reviews: ReviewAnalyticsDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    averageMinutes: 0,
    p90Minutes: 0,
    runs: 0,
    durations: [] as number[],
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const review of reviews) {
    if (review.trigger === "legacy_activity") continue;
    const start = review.started_at || review.created_at;
    const end = review.completed_at || review.provider_finished_at;
    if (!start || !end) continue;
    const duration = (new Date(end).getTime() - new Date(start).getTime()) / 60_000;
    const row = byDate.get(end.slice(0, 10));
    if (!row || !Number.isFinite(duration) || duration < 0) continue;
    row.durations.push(duration);
  }
  return rows.map((row) => {
    const durations = [...row.durations].sort((left, right) => left - right);
    const average = durations.reduce((total, value) => total + value, 0)
      / Math.max(1, durations.length);
    const p90Index = Math.max(0, Math.ceil(durations.length * 0.9) - 1);
    return {
      date: row.date,
      label: row.label,
      averageMinutes: Number(average.toFixed(1)),
      p90Minutes: Number((durations[p90Index] ?? 0).toFixed(1)),
      runs: durations.length,
    };
  });
}

export function reviewImpactTrend(
  reviews: ReviewAnalyticsDatum[],
  findings: FindingAnalyticsDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    findings: 0,
    resolutions: 0,
    pullRequests: new Set<string>(),
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const review of reviews) {
    const row = byDate.get(review.created_at.slice(0, 10));
    if (!row) continue;
    row.pullRequests.add(`${review.repository}#${review.number}`);
    const resolutions = Number(review.result?.threads_resolved ?? 0);
    if (Number.isFinite(resolutions) && resolutions > 0) {
      row.resolutions += resolutions;
    }
  }
  for (const finding of findings) {
    const row = byDate.get(finding.created_at.slice(0, 10));
    if (!row) continue;
    row.findings += 1;
    row.pullRequests.add(`${finding.repository}#${finding.number}`);
  }
  return rows.map((row) => ({
    date: row.date,
    label: row.label,
    findingsPerPr: Number((row.findings / Math.max(1, row.pullRequests.size)).toFixed(1)),
    resolutionsPerPr: Number((row.resolutions / Math.max(1, row.pullRequests.size)).toFixed(1)),
    findings: row.findings,
    resolutions: row.resolutions,
    pullRequests: row.pullRequests.size,
  }));
}

export function mergeLeadTimeTrend(
  completedPulls: CompletedPullDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    averageHours: 0,
    p90Hours: 0,
    merges: 0,
    durations: [] as number[],
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const pull of completedPulls) {
    if (pull.state !== "merged" || !pull.created_at) continue;
    const mergedAt = pull.merged_at || pull.completed_at;
    if (!mergedAt) continue;
    const hours = (Date.parse(mergedAt) - Date.parse(pull.created_at)) / 3_600_000;
    const row = byDate.get(mergedAt.slice(0, 10));
    if (!row || !Number.isFinite(hours) || hours < 0) continue;
    row.durations.push(hours);
  }
  return rows.map((row) => {
    const durations = [...row.durations].sort((left, right) => left - right);
    const average = durations.reduce((total, value) => total + value, 0)
      / Math.max(1, durations.length);
    const p90Index = Math.max(0, Math.ceil(durations.length * 0.9) - 1);
    return {
      date: row.date,
      label: row.label,
      averageHours: Number(average.toFixed(1)),
      p90Hours: Number((durations[p90Index] ?? 0).toFixed(1)),
      merges: durations.length,
    };
  });
}

export function findingPriorityTrend(
  findings: FindingAnalyticsDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    P0: 0,
    P1: 0,
    P2: 0,
    P3: 0,
    P4: 0,
    P5: 0,
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const finding of findings) {
    const row = byDate.get(finding.created_at.slice(0, 10));
    const priority = finding.priority;
    if (row && priority && priority in row) {
      row[priority as "P0" | "P1" | "P2" | "P3" | "P4" | "P5"] += 1;
    }
  }
  return rows;
}

export function providerAttemptTrend(
  events: ProviderEventDatum[],
  now = new Date(),
  days = 14,
) {
  const rows = utcDateRows(now, days, (date, label) => ({
    date,
    label,
    succeeded: 0,
    failed: 0,
  }));
  const byDate = new Map(rows.map((row) => [row.date, row]));
  for (const event of events) {
    if (!["provider.completed", "provider.failed"].includes(event.kind)) continue;
    const row = byDate.get(event.created_at.slice(0, 10));
    if (!row) continue;
    if (event.kind === "provider.completed") row.succeeded += 1;
    else row.failed += 1;
  }
  return rows;
}

export function reviewCycleDistribution(
  openPulls: Array<PullRequestDatum & { review_count?: number }>,
  completedPulls: Array<CompletedPullDatum & { review_count?: number }>,
  now = new Date(),
  days = 14,
) {
  const firstDay = new Date(now);
  firstDay.setUTCHours(0, 0, 0, 0);
  firstDay.setUTCDate(firstDay.getUTCDate() - (days - 1));
  const cycles = new Map<string, number>();
  for (const pull of openPulls) {
    if (!pull.created_at || Date.parse(pull.created_at) < firstDay.getTime()) continue;
    cycles.set(`${pull.repository}#${pull.number}`, pull.review_count ?? 0);
  }
  for (const pull of completedPulls) {
    const completedAt = pull.merged_at || pull.completed_at;
    if (!completedAt || Date.parse(completedAt) < firstDay.getTime()) continue;
    cycles.set(`${pull.repo}#${pull.number}`, pull.review_count ?? 0);
  }
  const rows = [
    { label: "1", pulls: 0 },
    { label: "2", pulls: 0 },
    { label: "3", pulls: 0 },
    { label: "4+", pulls: 0 },
  ];
  for (const count of cycles.values()) {
    if (count <= 0) continue;
    rows[Math.min(count, 4) - 1].pulls += 1;
  }
  return rows;
}

export function threadHealthByRepository(
  pulls: PullThreadDatum[],
  reviews: ReviewAnalyticsDatum[],
  now = new Date(),
  days = 14,
) {
  const firstDay = new Date(now);
  firstDay.setUTCHours(0, 0, 0, 0);
  firstDay.setUTCDate(firstDay.getUTCDate() - (days - 1));
  const rows = new Map<string, { repository: string; unresolved: number; resolved: number }>();
  const row = (repository: string) => {
    const current = rows.get(repository) ?? { repository, unresolved: 0, resolved: 0 };
    rows.set(repository, current);
    return current;
  };
  for (const pull of pulls) {
    row(pull.repository).unresolved += pull.unresolved_threads ?? 0;
  }
  for (const review of reviews) {
    if (Date.parse(review.created_at) < firstDay.getTime()) continue;
    row(review.repository).resolved += Number(review.result?.threads_resolved ?? 0) || 0;
  }
  return [...rows.values()]
    .filter((value) => value.unresolved > 0 || value.resolved > 0)
    .sort((left, right) =>
      right.unresolved + right.resolved - (left.unresolved + left.resolved)
    )
    .slice(0, 8)
    .map((value) => ({
      ...value,
      label: value.repository.split("/").at(-1) ?? value.repository,
    }));
}

export type TestReleaseDatum = {
  repository: string;
  release_tag: string;
  release_kind: string;
  commit_sha: string;
  released_at: string;
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  errored: number;
  duration_ms: number;
  source: string;
  provenance?: string | null;
  framework?: string | null;
  has_tests?: boolean;
  has_coverage?: boolean;
  lines_covered?: number | null;
  lines_valid?: number | null;
  line_rate?: number | null;
  branches_covered?: number | null;
  branches_valid?: number | null;
  branch_rate?: number | null;
  covered_files?: number | null;
  coverage_format?: string | null;
  /// How many independent pipelines reported for this release. A release whose
  /// shard count is below its predecessor's is missing a report, not shrinking.
  test_shards?: number;
  coverage_shards?: number;
  /// False while discovery still expects more reports for this release.
  settled?: boolean;
};

export type TestOwnerDatum = {
  owner: string;
  tests_owned: number;
  tests_created: number;
  tests_last_edited: number;
  failing_owned: number;
  skipped_owned: number;
  repositories: number;
  last_edited_at?: string | null;
};

export type TestRepositoryDatum = {
  repository: string;
  release_tag: string;
  released_at: string;
  tests: number;
  owned: number;
  unattributed: number;
  without_file: number;
  attribution_errors: number;
};

/// A failing test's outcome counts as "failing" whether the runner reported it
/// as a failure or an error: both mean the test did not pass.
function failingCount(release: TestReleaseDatum) {
  return release.failed + release.errored;
}

function shortRepository(repository: string) {
  return repository.split("/").at(-1) ?? repository;
}

function percent(covered: number, valid: number) {
  return valid > 0 ? Number(((covered / valid) * 100).toFixed(1)) : 0;
}

function releasedOrder(left: TestReleaseDatum, right: TestReleaseDatum) {
  return (
    Date.parse(left.released_at) - Date.parse(right.released_at)
    || left.release_tag.localeCompare(right.release_tag)
  );
}

/// Releases for one repository, oldest first, with the growth in test count
/// against the immediately preceding release of that same repository.
///
/// Only releases carrying a test report are included: a coverage-only release
/// would otherwise read as a suite that dropped to zero tests.
export function testReleaseSeries(
  releases: TestReleaseDatum[],
  repository?: string,
) {
  const selected = releases
    .filter((release) => !repository || release.repository === repository)
    .filter((release) => release.has_tests !== false)
    .slice()
    .sort((left, right) =>
      Date.parse(left.released_at) - Date.parse(right.released_at)
      || left.release_tag.localeCompare(right.release_tag)
    );
  const previousByRepository = new Map<string, TestReleaseDatum>();
  return selected.map((release) => {
    const previous = previousByRepository.get(release.repository);
    previousByRepository.set(release.repository, release);
    const failing = failingCount(release);
    return {
      repository: release.repository,
      label: release.release_tag,
      releaseKind: release.release_kind,
      releasedAt: release.released_at,
      commitSha: release.commit_sha,
      source: release.source,
      total: release.total,
      passed: release.passed,
      failing,
      skipped: release.skipped,
      passRate: release.total
        ? Number(((release.passed / release.total) * 100).toFixed(1))
        : 0,
      durationMinutes: Number((release.duration_ms / 60_000).toFixed(1)),
      addedTests: previous ? Math.max(0, release.total - previous.total) : release.total,
      removedTests: previous ? Math.max(0, previous.total - release.total) : 0,
      netChange: previous ? release.total - previous.total : release.total,
      shards: release.test_shards ?? 0,
      gathering: release.settled === false,
      // A drop in reporting pipelines explains a drop in tests. Without this
      // the two are indistinguishable on the chart.
      missingShards: Math.max(0, (previous?.test_shards ?? 0) - (release.test_shards ?? 0)),
    };
  });
}

/// The newest reported release per repository, with its change against that
/// repository's previous release. This is the cross-repository comparison.
export function testTotalsByRepository(releases: TestReleaseDatum[]) {
  const byRepository = new Map<string, TestReleaseDatum[]>();
  for (const release of releases) {
    // Tests and coverage are ingested independently, so a release can carry
    // coverage and no test report. Counting one of those as the repository's
    // latest suite reports zero tests and a net change of minus the whole
    // suite — a reporting gap read as a catastrophe.
    if (release.has_tests === false) continue;
    const bucket = byRepository.get(release.repository) ?? [];
    bucket.push(release);
    byRepository.set(release.repository, bucket);
  }
  return [...byRepository.entries()]
    .map(([repository, bucket]) => {
      // Same ordering as the API's, tag included: releases cut in the same
      // second are common, and the tile and the test list underneath it must
      // agree on which one is latest.
      const ordered = bucket.slice().sort(releasedOrder);
      const latest = ordered[ordered.length - 1];
      const previous = ordered[ordered.length - 2];
      const failing = failingCount(latest);
      return {
        repository,
        label: shortRepository(repository),
        releaseTag: latest.release_tag,
        releasedAt: latest.released_at,
        releases: ordered.length,
        total: latest.total,
        passed: latest.passed,
        failing,
        skipped: latest.skipped,
        passRate: latest.total
          ? Number(((latest.passed / latest.total) * 100).toFixed(1))
          : 0,
        previousTotal: previous?.total ?? null,
        netChange: previous ? latest.total - previous.total : null,
        shards: latest.test_shards ?? 0,
        // A release still gathering reports, or one whose shard count dropped
        // against the previous release, is not yet a like-for-like comparison.
        gathering: latest.settled === false,
        missingShards: Math.max(0, (previous?.test_shards ?? 0) - (latest.test_shards ?? 0)),
      };
    })
    .sort((left, right) => right.total - left.total);
}

/// Coverage for one repository over its releases, oldest first, with the change
/// against the previous release that reported coverage.
export function coverageReleaseSeries(
  releases: TestReleaseDatum[],
  repository?: string,
) {
  const selected = releases
    .filter((release) => !repository || release.repository === repository)
    .filter((release) => release.has_coverage && (release.lines_valid ?? 0) > 0)
    .slice()
    .sort(releasedOrder);
  const previousRate = new Map<string, number>();
  return selected.map((release) => {
    const lineRate = percent(release.lines_covered ?? 0, release.lines_valid ?? 0);
    const previous = previousRate.get(release.repository);
    if (release.settled !== false) previousRate.set(release.repository, lineRate);
    return {
      repository: release.repository,
      label: release.release_tag,
      releasedAt: release.released_at,
      lineRate,
      branchRate: release.branch_rate ?? null,
      linesCovered: release.lines_covered ?? 0,
      linesValid: release.lines_valid ?? 0,
      uncoveredLines: (release.lines_valid ?? 0) - (release.lines_covered ?? 0),
      files: release.covered_files ?? 0,
      format: release.coverage_format ?? null,
      gathering: release.settled === false,
      rateChange: release.settled === false || previous === undefined
        ? null
        : Number((lineRate - previous).toFixed(1)),
    };
  });
}

/// Every repository's newest reported coverage, worst first, with its change.
/// This is the cross-repository "coverage across repos" comparison.
export function coverageByRepository(releases: TestReleaseDatum[]) {
  const byRepository = new Map<string, TestReleaseDatum[]>();
  for (const release of releases) {
    if (!release.has_coverage || (release.lines_valid ?? 0) <= 0) continue;
    const bucket = byRepository.get(release.repository) ?? [];
    bucket.push(release);
    byRepository.set(release.repository, bucket);
  }
  return [...byRepository.entries()]
    .map(([repository, bucket]) => {
      const ordered = bucket.slice().sort(releasedOrder);
      const latest = ordered[ordered.length - 1];
      const previous = ordered[ordered.length - 2];
      const lineRate = percent(latest.lines_covered ?? 0, latest.lines_valid ?? 0);
      return {
        repository,
        label: shortRepository(repository),
        releaseTag: latest.release_tag,
        releasedAt: latest.released_at,
        lineRate,
        branchRate: latest.branch_rate ?? null,
        linesCovered: latest.lines_covered ?? 0,
        linesValid: latest.lines_valid ?? 0,
        uncoveredLines: (latest.lines_valid ?? 0) - (latest.lines_covered ?? 0),
        files: latest.covered_files ?? 0,
        reports: ordered.length,
        gathering: latest.settled === false,
        rateChange: latest.settled !== false && previous
          ? Number(
            (lineRate - percent(previous.lines_covered ?? 0, previous.lines_valid ?? 0))
              .toFixed(1),
          )
          : null,
      };
    })
    .sort((left, right) => left.lineRate - right.lineRate);
}

/// The whole portfolio over time: at each date any repository released, the sum
/// of every repository's most recent report as of that date.
///
/// Repositories release on their own schedules, so a plain group-by-date would
/// make the totals lurch as each one reports. Carrying the last known value
/// forward per repository is what makes a cross-repository trend meaningful.
export function portfolioTrend(releases: TestReleaseDatum[]) {
  const ordered = releases.slice().sort(releasedOrder);
  const dates = [...new Set(ordered.map((release) => release.released_at.slice(0, 10)))].sort();
  const latestTests = new Map<string, TestReleaseDatum>();
  const latestCoverage = new Map<string, TestReleaseDatum>();
  let cursor = 0;
  return dates.map((date) => {
    while (
      cursor < ordered.length && ordered[cursor].released_at.slice(0, 10) <= date
    ) {
      const release = ordered[cursor];
      if (release.has_tests !== false) latestTests.set(release.repository, release);
      if (release.has_coverage && (release.lines_valid ?? 0) > 0) {
        latestCoverage.set(release.repository, release);
      }
      cursor += 1;
    }
    let total = 0;
    let passed = 0;
    let failing = 0;
    for (const release of latestTests.values()) {
      total += release.total;
      passed += release.passed;
      failing += release.failed + release.errored;
    }
    let linesCovered = 0;
    let linesValid = 0;
    for (const release of latestCoverage.values()) {
      linesCovered += release.lines_covered ?? 0;
      linesValid += release.lines_valid ?? 0;
    }
    return {
      date,
      label: new Date(`${date}T00:00:00Z`).toLocaleDateString("en", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
      repositoriesWithTests: latestTests.size,
      repositoriesWithCoverage: latestCoverage.size,
      total,
      passed,
      failing,
      passRate: percent(passed, total),
      // Weighted by lines, not an average of percentages: a 20-line repository
      // must not swing the portfolio as much as a 20,000-line one.
      lineRate: percent(linesCovered, linesValid),
      linesCovered,
      linesValid,
    };
  });
}

/// Cross-repository headline numbers: totals over every repository's newest
/// report, plus how many repositories are reporting at all.
export function portfolioSummary(
  releases: TestReleaseDatum[],
  repositories: TestRepositoryDatum[],
  owners: TestOwnerDatum[],
) {
  const tests = testQualitySummary(releases, repositories, owners);
  const coverage = coverageByRepository(releases);
  const linesCovered = coverage.reduce((sum, row) => sum + row.linesCovered, 0);
  const linesValid = coverage.reduce((sum, row) => sum + row.linesValid, 0);
  const reporting = new Set(releases.map((release) => release.repository));
  return {
    ...tests,
    reportingRepositories: reporting.size,
    coverageRepositories: coverage.length,
    lineRate: percent(linesCovered, linesValid),
    linesCovered,
    linesValid,
    uncoveredLines: linesValid - linesCovered,
    // Named so the gap is actionable rather than implied by two other numbers.
    repositoriesWithoutCoverage: reporting.size - coverage.length,
    worstCovered: coverage.at(0) ?? null,
    bestCovered: coverage.at(-1) ?? null,
  };
}

/// The Overview page's condensed coverage read: the repositories dragging the
/// portfolio down, the trend behind the headline, and how far that trend moved
/// across the window Overview shows.
///
/// Overview and the Test quality portfolio must agree to the digit, so this
/// slices what `coverageByRepository` and `portfolioTrend` already produced
/// rather than aggregating a second time. The two coverage readings Netra
/// carried before diverged on which reports each one selected, not on the
/// arithmetic applied afterwards, and a second aggregation is exactly how that
/// happens again.
export function overviewCoverageDigest(
  releases: TestReleaseDatum[],
  options: { repositories?: number; points?: number } = {},
) {
  const repositoryLimit = Math.max(0, options.repositories ?? 6);
  const pointLimit = Math.max(0, options.points ?? 12);
  const ranked = coverageByRepository(releases);
  // A date carrying no measured lines is a tests-only release, not a portfolio
  // that dropped to zero percent.
  const reported = portfolioTrend(releases).filter((point) => point.linesValid > 0);
  const trend = pointLimit > 0 ? reported.slice(-pointLimit) : reported;
  const opening = trend.at(0);
  const latest = trend.at(-1);
  return {
    // Worst first: Overview has room for a handful, and the handful worth the
    // space is the one a reader can act on.
    lowestCovered: ranked.slice(0, repositoryLimit),
    /// Repositories that report coverage but did not fit the chart, so the
    /// chart never reads as the whole portfolio.
    moreRepositories: Math.max(0, ranked.length - repositoryLimit),
    trend,
    /// Null rather than zero when there is nothing to compare against: "flat"
    /// and "only one report so far" are different answers.
    rateChange: opening && latest && trend.length > 1
      ? Number((latest.lineRate - opening.lineRate).toFixed(1))
      : null,
  };
}

/// Who owns the tests. `testsOwned` is authorship of the most lines currently
/// in the test; creating and last editing are tracked separately because they
/// are usually different people.
export function testOwnershipLeaderboard(owners: TestOwnerDatum[], limit = 12) {
  return owners
    .filter((owner) => owner.owner)
    .map((owner) => ({
      owner: owner.owner,
      label: owner.owner,
      testsOwned: owner.tests_owned,
      testsCreated: owner.tests_created,
      testsLastEdited: owner.tests_last_edited,
      failingOwned: owner.failing_owned,
      repositories: owner.repositories,
      lastEditedAt: owner.last_edited_at ?? null,
    }))
    .sort((left, right) =>
      right.testsOwned - left.testsOwned
      || right.testsCreated - left.testsCreated
      || left.owner.localeCompare(right.owner)
    )
    .slice(0, limit);
}

/// Attribution is a three-way split, not a yes/no: a test can be genuinely
/// unowned, or simply not attributed yet. Collapsing those would overstate how
/// much of a suite has no owner.
export function testOwnershipCoverage(repositories: TestRepositoryDatum[]) {
  const totals = repositories.reduce(
    (accumulator, repository) => ({
      tests: accumulator.tests + repository.tests,
      owned: accumulator.owned + repository.owned,
      unattributed: accumulator.unattributed + repository.unattributed,
    }),
    { tests: 0, owned: 0, unattributed: 0 },
  );
  const unowned = Math.max(0, totals.tests - totals.owned - totals.unattributed);
  return {
    ...totals,
    unowned,
    ownedPercent: totals.tests
      ? Number(((totals.owned / totals.tests) * 100).toFixed(1))
      : 0,
  };
}

/// Headline numbers for the test-quality tiles, taken from each repository's
/// newest reported release so the totals describe one coherent moment.
export function testQualitySummary(
  releases: TestReleaseDatum[],
  repositories: TestRepositoryDatum[],
  owners: TestOwnerDatum[],
) {
  const latest = testTotalsByRepository(releases);
  const total = latest.reduce((sum, row) => sum + row.total, 0);
  const passed = latest.reduce((sum, row) => sum + row.passed, 0);
  const failing = latest.reduce((sum, row) => sum + row.failing, 0);
  const skipped = latest.reduce((sum, row) => sum + row.skipped, 0);
  const added = latest.reduce((sum, row) => sum + Math.max(0, row.netChange ?? 0), 0);
  const removed = latest.reduce((sum, row) => sum + Math.max(0, -(row.netChange ?? 0)), 0);
  const coverage = testOwnershipCoverage(repositories);
  return {
    repositories: latest.length,
    total,
    passed,
    failing,
    skipped,
    added,
    removed,
    passRate: total ? Number(((passed / total) * 100).toFixed(1)) : 0,
    contributors: owners.filter((owner) => owner.tests_owned > 0).length,
    ownedPercent: coverage.ownedPercent,
    unowned: coverage.unowned,
    unattributed: coverage.unattributed,
  };
}

export function outcomeBreakdown(reviews: ReviewDatum[]) {
  const categories = [
    { name: "Completed", color: "#48d597", matches: ["completed"] },
    { name: "Active", color: "#67a8ff", matches: ["queued", "running", "finalizing"] },
    { name: "Failed", color: "#ff6b7a", matches: ["failed", "aborted"] },
  ];
  return categories
    .map(({ name, color, matches }) => ({
      name,
      color,
      value: reviews.filter((review) => matches.includes(review.status)).length,
    }))
    .filter((row) => row.value > 0);
}

export function providerReliability(providers: ProviderDatum[]) {
  return providers.map((provider) => ({
    provider: provider.provider,
    invocations: provider.invocations,
    successRate: provider.invocations
      ? Math.round((provider.successes / provider.invocations) * 100)
      : 0,
    failures: provider.failures,
  }));
}
